/**
 * Module i18n Nestor — point d'entrée public.
 *
 * Phase 1 (incrément 1) : port direct de l'objet `TR` du prototype, avec
 * une API minimale `t(key)` + `setLang(lang)` + `subscribeLang(listener)`.
 *
 * Phase 8 (i18n production) : ce fichier est swap-in pour i18next sans
 * toucher aux call sites. Garder la signature `t: (key: TKey) => string`
 * stable.
 *
 * **Règle absolue** (cf. CLAUDE.md §3.6) : aucune chaîne FR visible par
 * l'utilisateur ne doit être codée en dur dans les modules. Tout passe par
 * `t(key)`. Le script `scripts/check-i18n-leaks.sh` est exécuté en
 * pre-commit pour traquer les fuites.
 */
import { fr, getDictionary, type TKey } from './strings.js';
import { getLang } from './lang.js';

export { SUPPORTED_LANGS, DEFAULT_LANG, type Lang, type TKey } from './strings.js';
export {
  getLang,
  setLang,
  subscribeLang,
  detectLang,
  readStoredLang,
  _resetLangForTests,
} from './lang.js';

/**
 * Traduit une clé i18n. Stratégie de fallback (différente du proto qui
 * retournait `undefined` sur clé manquante) :
 *
 *   1. Dictionnaire de la langue courante.
 *   2. Dictionnaire FR (source de vérité).
 *   3. La clé brute (utile en dev pour repérer les clés manquantes).
 *
 * Cette robustesse évite les `undefined` visibles dans l'UI quand une
 * langue secondaire (pt, it, pl) n'a pas traduit une clé. C'est une
 * **amélioration intentionnelle** par rapport au proto, validée
 * 2026-04-27 (n'altère aucun libellé FR existant).
 */
export function t(key: TKey): string {
  const lang = getLang();
  // Phase 8C : les langues secondaires sont lazy — tant que leur chunk
  // n'est pas arrivé, getDictionary retourne undefined et on sert le FR.
  const localized = getDictionary(lang)?.[key];
  if (localized !== undefined) return localized;

  const fallback = fr[key];
  if (fallback !== undefined) return fallback;

  return key;
}

/**
 * Bases de pluralisation : toute base `x` telle que `x_one` ET
 * `x_other` existent dans le dictionnaire FR. Ajouter une paire de
 * clés suffit à rendre la base utilisable dans `tn()` — le type est
 * dérivé, rien à déclarer.
 */
type PluralBase = {
  [K in TKey]: K extends `${infer B}_one` ? (`${B}_other` extends TKey ? B : never) : never;
}[TKey];

/**
 * Pluralisation maison (audit C) : sélectionne `_one` / `_other` via
 * `Intl.PluralRules` de la langue courante — « 1 entrée / 2 entrées »,
 * « 0 entrée » en FR (zéro est singulier en français) mais
 * « 0 entries » en EN. Limite assumée : les catégories `few` / `many`
 * (polonais) retombent sur `_other` — compréhensible, à affiner si un
 * locuteur PL le signale.
 */
export function tn(base: PluralBase, count: number): string {
  let category: Intl.LDMLPluralRule = 'other';
  try {
    category = new Intl.PluralRules(getLang()).select(count);
  } catch {
    // Environnement sans ICU complet : défaut « other ».
  }
  return t(category === 'one' ? `${base}_one` : `${base}_other`);
}
